import { Table } from '@dynamika-shared/ui/Table/Table';
import {
  InvitationActionType,
  makeInvitationsColumns,
} from './InvitationsTable.columns';
import { FC, useState } from 'react';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { SubPageHeader } from '@components/SubPageHeader/SubPageHeader';
import { Select } from '@dynamika-shared/ui/Select/Select';

import type { Invitation } from '@restore/invitationsUserManagement/invitationsUserManagement.types';
import { CreateInvitation } from '../CreateInvitation/CreateInvitation';
import { RevokeInvitationModal } from '../RevokeInvitation/RevokeInvitationModal';

type ActionMode = 'create' | 'revoke';

type SelectOption = { label: string; value: string };

export type InvitationsListViewProps = {
  loading?: boolean;
  invitationsData: Invitation[];
  facilityMap: Record<string, string>;
  sponsorOptions: SelectOption[];
  selectedSponsorId?: string;
  onSponsorChange: (sponsorId: string) => void;
  refetchInvitations: () => void;
};

export const InvitationsListView: FC<InvitationsListViewProps> = ({
  invitationsData,
  loading,
  facilityMap,
  sponsorOptions,
  selectedSponsorId,
  onSponsorChange,
  refetchInvitations,
}) => {
  const [currentInvitation, setCurrentInvitation] = useState<Invitation>();
  const [actionMode, setActionMode] = useState<ActionMode>();

  const invitationsColumns = makeInvitationsColumns({
    facilityMap,
    onInvitationClick: (invitation, actionType) => {
      if (actionType === InvitationActionType.revoke) {
        setActionMode('revoke');
        setCurrentInvitation(invitation);
      }
    },
  });

  const closeAction = () => {
    setActionMode(undefined);
    setCurrentInvitation(undefined);
  };

  return (
    <div
      data-testid='InvitationsPageView'
      className='relative h-[calc(100%-120px)]'
    >
      <SubPageHeader
        title='Invitations List'
        buttonText='Invite'
        showButton
        action={() => setActionMode('create')}
      >
        <Select
          showSearch
          filterOption={(input, option) =>
            String(option?.label ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          options={sponsorOptions}
          value={selectedSponsorId}
          onChange={onSponsorChange}
          placeholder='Select Sponsor'
          className='w-[30rem]'
          data-testid='sponsorFilter'
        />
      </SubPageHeader>
      <div className='mt-3 flex grow flex-col'>
        <Table
          columns={invitationsColumns}
          data={invitationsData}
          totalItems={invitationsData.length}
          loading={loading}
          noContentMessage={
            <NoContent
              action={() => setActionMode('create')}
              actionText={'Invite User'}
              iconName={'subjects'}
              title={
                <span className='text-1424 text-spanishGray'>
                  No{' '}
                  <span className='font-semibold text-chineseBlack'>
                    Invitations
                  </span>{' '}
                  here yet! <br /> Click the button to invite a user!
                </span>
              }
            />
          }
        />
      </div>
      {actionMode === 'create' && selectedSponsorId && (
        <CreateInvitation
          close={closeAction}
          refetchInvitations={refetchInvitations}
          sponsor={{
            id: selectedSponsorId,
            name:
              sponsorOptions.find(o => o.value === selectedSponsorId)?.label ??
              '',
          }}
        />
      )}
      {actionMode === 'revoke' && currentInvitation && (
        <RevokeInvitationModal
          invitation={currentInvitation}
          refetchInvitations={refetchInvitations}
          onClose={closeAction}
        />
      )}
    </div>
  );
};
